import React, { useState, useRef, useCallback, useEffect } from 'react'
import { preuploadPhoto } from '../utils/preuploadPhoto'
import { useNavigate, useLocation } from 'react-router-dom'
import InstagramCropper from '../components/InstagramCropper'
import RotationDial from '../components/RotationDial'
import {
  CropTransform,
  cropSizeFor,
  drawCrop,
  outputSizeFor,
} from '../utils/cropGeometry'
import AppBar from '../components/AppBar'
import { useReviewStore } from '../stores/reviewStore'
import { PhotoItem, PhotoCropState } from '../types/photo'
import { MAX_PHOTOS } from '../constants/layout'
import { dataURLtoFile } from '../utils/fileUtils'
import { devDebug } from '../utils/devDebug'

/** iOS WKWebView 등에서 초대형 캔버스면 toDataURL이 data:, 로 비는 경우가 있어 상한 둠 */
const MAX_CROP_EXPORT_SIDE = 4096

const isNonEmptyImageDataUrl = (dataUrl: string): boolean => {
  if (!dataUrl.startsWith('data:image')) {
    return false
  }
  const comma = dataUrl.indexOf(',')
  if (comma < 0) {
    return false
  }
  return dataUrl.slice(comma + 1).trim().length > 0
}

const logPhotoEdit = (stage: string, detail: Record<string, unknown>) => {
  devDebug.log('[PhotoEdit]', { stage, ...detail })
}

interface CroppedAreaPixels {
  x: number
  y: number
  width: number
  height: number
}

const ASPECT_RATIOS = [
  { label: '1:1', value: 1 },
  { label: '4:5', value: 4 / 5 },
  { label: '5:4', value: 5 / 4 },
]

const PhotoEdit: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const file = location.state?.file as File | undefined
  const preview = location.state?.preview as string | undefined
  const editPhotoId = location.state?.editPhotoId as string | undefined
  const editPhotoIndex = location.state?.editPhotoIndex as number | undefined
  // 재편집 복원: 이전 크롭 상태(비율·팬·줌)
  const initialCropState = location.state?.initialCropState as
    | PhotoCropState
    | undefined

  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [aspect, setAspect] = useState<number | undefined>(
    initialCropState?.aspect ?? 4 / 5
  )
  const [selectedRatioIndex, setSelectedRatioIndex] = useState(
    initialCropState?.ratioIndex ?? 1
  )
  const [rotation, setRotation] = useState(initialCropState?.rotation ?? 0)
  // 크로퍼가 보고하는 현재 팬/줌(저장용)
  const transformRef = useRef<{ pos: { x: number; y: number }; zoom: number }>({
    pos: initialCropState?.pos ?? { x: 0, y: 0 },
    zoom: initialCropState?.zoom ?? 1,
  })
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<CroppedAreaPixels | null>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const entryLoggedRef = useRef(false)
  const cropContainerRef = useRef<HTMLDivElement>(null)
  const [mediaAspect, setMediaAspect] = useState<number | null>(null)

  // 원본 이미지 비율 감지
  useEffect(() => {
    if (!preview) return
    const img = new Image()
    img.onload = () => {
      const imgAspect = img.naturalWidth / img.naturalHeight
      setMediaAspect(imgAspect)
      // 재편집 복원 중이면 저장된 비율을 유지(자동감지로 덮어쓰지 않음)
      if (!initialCropState && imgAspect >= 1) {
        setAspect(1)
        setSelectedRatioIndex(0)
      }
    }
    img.src = preview
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview])

  const onCropComplete = useCallback(
    (croppedAreaPx: CroppedAreaPixels) => {
      setCroppedAreaPixels(croppedAreaPx)
    },
    []
  )

  // 크로퍼의 현재 팬/줌 보고 → 저장용 ref에 반영
  const handleTransformChange = useCallback(
    (t: { pos: { x: number; y: number }; zoom: number }) => {
      transformRef.current = t
    },
    []
  )

  // 파일이 없으면 뒤로가기
  if (!file || !preview) {
    return (
      <div className="app-container w-full h-full flex flex-col bg-backgroundColorBg2">
        <AppBar title="사진 편집" onBack={() => navigate(-1)} isBottomBorder />
        <div className="flex-1 flex items-center justify-center">
          <span className="text-style-body1 text-textColorText2">
            사진을 불러오는데 실패했습니다.
          </span>
        </div>
      </div>
    )
  }

  if (!entryLoggedRef.current) {
    entryLoggedRef.current = true
    logPhotoEdit('entry', {
      fileName: file.name,
      fileType: file.type,
      inputPreview: devDebug.dataUrlMeta(preview),
    })
  }

  const handleBack = () => {
    navigate(-1)
  }

  const handleRatioChange = (index: number) => {
    setSelectedRatioIndex(index)
    setAspect(ASPECT_RATIOS[index].value)
    setZoom(1)
    setCrop({ x: 0, y: 0 })
  }

  const pushUncroppedPhoto = (reason: string) => {
    logPhotoEdit('save_uncropped', {
      reason,
      outputPreview: devDebug.dataUrlMeta(preview),
    })
    addPhotoToStore(
      {
        id: `${Date.now()}-${Math.random()}`,
        file,
        preview,
        description: '',
        originalPreview: preview,
        originalFile: file,
      },
      'uncropped'
    )
  }

  const handleComplete = async () => {
    logPhotoEdit('complete_tap', {
      hasCroppedAreaPixels: Boolean(croppedAreaPixels),
    })

    if (!croppedAreaPixels || !canvasRef.current) {
      pushUncroppedPhoto('no_cropped_area_or_canvas')
      return
    }

    // 이미지 로드
    const image = new Image()
    image.crossOrigin = 'anonymous'

    try {
      await new Promise<void>((resolve, reject) => {
        image.onload = () => resolve()
        image.onerror = () => reject(new Error('Image load failed'))
        image.src = preview
      })
    } catch {
      pushUncroppedPhoto('image_load_failed')
      return
    }

    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      pushUncroppedPhoto('no_canvas_2d_context')
      return
    }

    // 화면과 똑같이 그린다.
    //
    // 회전이 들어오면 '원본에서 사각형 하나를 오려낸다'로는 결과를 만들 수 없다.
    // 그래서 미리보기와 같은 함수(drawCrop)로 같은 변환을 적용한다 —
    // 이렇게 해야 "화면에서 본 것과 저장된 사진이 다르다"가 안 생긴다.
    const container = cropContainerRef.current
    const crop = cropSizeFor(
      container?.clientWidth ?? 0,
      container?.clientHeight ?? 0,
      aspect || 4 / 5
    )
    if (crop.w === 0 || crop.h === 0) {
      pushUncroppedPhoto('no_crop_size')
      return
    }

    const t: CropTransform = {
      cropW: crop.w,
      cropH: crop.h,
      pos: transformRef.current.pos,
      zoom: transformRef.current.zoom,
      rotation,
      naturalW: image.naturalWidth,
      naturalH: image.naturalHeight,
    }

    const out = outputSizeFor(t, MAX_CROP_EXPORT_SIDE)
    const outW = out.w
    const outH = out.h
    const scaledDown =
      outW >= MAX_CROP_EXPORT_SIDE || outH >= MAX_CROP_EXPORT_SIDE

    canvas.width = outW
    canvas.height = outH

    ctx.setTransform(1, 0, 0, 1, 0, 0)
    ctx.imageSmoothingQuality = 'high'
    drawCrop(ctx, image, t, 0, 0, outW, outH)

    let base64 = canvas.toDataURL('image/jpeg', 0.92)
    let exportMime = 'image/jpeg'
    if (!isNonEmptyImageDataUrl(base64)) {
      base64 = canvas.toDataURL('image/png')
      exportMime = 'image/png'
    }
    if (!isNonEmptyImageDataUrl(base64)) {
      logPhotoEdit('export_empty_data_url', {
        canvasOut: { w: outW, h: outH },
        scaledDown,
      })
      pushUncroppedPhoto('empty_toDataURL_after_jpeg_and_png')
      return
    }

    logPhotoEdit('export_ok', {
      croppedAreaPixels,
      rotation,
      canvasOut: { w: outW, h: outH },
      scaledDown,
      maxSide: MAX_CROP_EXPORT_SIDE,
      exportMime,
      outputDataUrl: devDebug.dataUrlMeta(base64),
    })

    const croppedFile = dataURLtoFile(base64, file.name)

    addPhotoToStore(
      {
        id: `${Date.now()}-${Math.random()}`,
        file: croppedFile,
        preview: base64,
        description: '',
        originalPreview: preview,
        originalFile: file,
        cropState: {
          pos: transformRef.current.pos,
          zoom: transformRef.current.zoom,
          aspect: aspect || 4 / 5,
          ratioIndex: selectedRatioIndex,
          rotation,
        },
      },
      'cropped'
    )
  }

  const addPhotoToStore = (
    newPhoto: PhotoItem,
    mode: 'cropped' | 'uncropped'
  ) => {
    const { photos: latestPhotos, setPhotos: set } = useReviewStore.getState()

    // 재편집 모드: 기존 사진 교체
    if (editPhotoId && editPhotoIndex !== undefined) {
      const next = [...latestPhotos]
      // 다시 편집했으면 사진이 바뀐 것이라, 전에 올려둔 주소는 버리고 새로 올린다.
      next[editPhotoIndex] = { ...newPhoto, id: editPhotoId, uploadedUrl: undefined }
      set(next)
      preuploadPhoto(editPhotoId, newPhoto.preview)
      logPhotoEdit('store_replace', {
        mode,
        editPhotoId,
        editPhotoIndex,
      })
      navigate(-1)
      return
    }

    if (latestPhotos.length >= MAX_PHOTOS) {
      logPhotoEdit('store_skipped_max', {
        max: MAX_PHOTOS,
        mode,
      })
      navigate(-1)
      return
    }

    const next = [...latestPhotos, newPhoto]
    set(next)
    // 저장을 기다리게 하지 않으려고 지금부터 올린다
    preuploadPhoto(newPhoto.id, newPhoto.preview)
    logPhotoEdit('store_commit', {
      mode,
      beforeCount: latestPhotos.length,
      afterCount: next.length,
      newPhotoPreview: devDebug.dataUrlMeta(newPhoto.preview),
    })
    navigate(-1)
  }

  return (
    <div className="app-container w-full h-full flex flex-col bg-backgroundColorBg2">
      <AppBar
        title="사진 편집"
        actionSize={64}
        rightElement={
          <button
            type="button"
            onClick={handleComplete}
            className="px-12px py-4px hover:opacity-70 whitespace-nowrap"
          >
            <span className="font-pretendard font-bold text-16px leading-24px text-[#6B5E46] text-center whitespace-nowrap">
              완료
            </span>
          </button>
        }
        onBack={handleBack}
        isBottomBorder
      />

      {/* 크롭 영역 — 4:5 비율 고정 */}
      <div
        ref={cropContainerRef}
        className="w-full relative overflow-hidden"
        style={{ aspectRatio: '4 / 5', maxHeight: '65vh', background: 'var(--backgroundColorBg2, #F5F0E8)' }}
      >
        <InstagramCropper
          image={preview}
          aspect={aspect || 4 / 5}
          onCropComplete={onCropComplete}
          initialPos={initialCropState?.pos}
          initialZoom={initialCropState?.zoom}
          rotation={rotation}
          onTransformChange={handleTransformChange}
        />
      </div>

      {/* 비율 선택 버튼 */}
      <div className="w-full bg-backgroundColorBg2 py-16px px-16px">
        <div className="flex justify-around">
          {ASPECT_RATIOS.map((ratio, index) => (
              <button
                key={ratio.label}
                type="button"
                onClick={() => handleRatioChange(index)}
                className="font-pretendard text-14px py-4px"
                style={{
                  color: selectedRatioIndex === index ? '#6B5E46' : '#A89F91',
                  fontWeight: selectedRatioIndex === index ? 700 : 400,
                  cursor: 'pointer',
                }}
              >
                {ratio.label}
              </button>
          ))}
        </div>
      </div>

      {/* 각도 다이얼 — 아이폰 사진 편집과 같은 눈금 방식 */}
      <RotationDial value={rotation} onChange={setRotation} />

      {/* 숨겨진 canvas */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  )
}

export default PhotoEdit
